import type { TFunction } from 'i18next';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';
import { Button } from '@/components/ui/button';
import { RowActionsMenu, type RowAction } from '@/components/ui/row-actions-menu';
import { formatDate } from '@/lib/intl';
import { cn } from '@/lib/utils';
import type { useAccessActions } from '../people/person-actions';
import { useRoleText } from '../roles/role-text';
import { Avatar, useExpiryText } from '../ui/person-bits';
import { INVITATION_TONE, type Invitation } from './use-invitations';

type Actions = ReturnType<typeof useAccessActions>;
type Translate = TFunction<'administration'>;

/** The line under a row's status: when it expires, expired, was accepted or withdrawn. */
export function useWhenText(invitation: Invitation): string | null {
  const { t } = useTranslation('administration');
  const expiry = useExpiryText();
  const { status } = invitation;
  if (status === 'pending') return expiry(invitation.expiresAt);
  if (status === 'expired') {
    return t('invitations.expiredOn', { date: formatDate(invitation.expiresAt) });
  }
  if (invitation.resolvedAt === null) return null;
  return t(status === 'accepted' ? 'invitations.acceptedOn' : 'invitations.withdrawnOn', {
    date: formatDate(invitation.resolvedAt),
  });
}

/** What goes behind «···» on a row: the acts that are not its one visible act. */
function moreActions(
  t: Translate,
  invitation: Invitation,
  actions: Actions,
  onEditRoles: (invitation: Invitation) => void,
): RowAction[] {
  const pending = invitation.status === 'pending';
  return [
    // Decision 151: while pending, with the standing to withdraw it, its roles can be changed.
    ...(pending && invitation.can.withdraw
      ? [
          {
            id: 'editRoles',
            label: t('invitations.editRoles'),
            onSelect: () => onEditRoles(invitation),
          },
        ]
      : []),
    ...(pending && invitation.can.renew
      ? [
          {
            id: 'renew',
            label: t('invitations.renew'),
            busy: actions.isPending(`renew:${invitation.id}`),
            onSelect: () => void actions.renew(invitation),
          },
        ]
      : []),
  ];
}

function InvitationRowActions({
  invitation,
  actions,
  onEditRoles,
  peopleHref,
}: {
  readonly invitation: Invitation;
  readonly actions: Actions;
  readonly onEditRoles: (invitation: Invitation) => void;
  readonly peopleHref: string;
}) {
  const { t } = useTranslation('administration');
  const { status } = invitation;
  // The row's one visible act, chosen by its state; anything else goes behind «···».
  const withdrawing = actions.isPending(`withdraw:${invitation.id}`);
  const renewing = actions.isPending(`renew:${invitation.id}`);
  const more = moreActions(t, invitation, actions, onEditRoles);
  return (
    <span className="people-row-actions gap-1">
      {status === 'pending' && invitation.can.withdraw ? (
        <Button
          variant="outline"
          size="sm"
          disabled={withdrawing}
          onClick={() => void actions.withdraw(invitation)}
        >
          {t('invitations.withdraw')}
        </Button>
      ) : null}
      {(status === 'expired' || status === 'withdrawn') && invitation.can.renew ? (
        <Button
          variant="outline"
          size="sm"
          disabled={renewing}
          onClick={() => void actions.renew(invitation)}
        >
          <Send />
          {t('invitations.renew')}
        </Button>
      ) : null}
      {status === 'accepted' ? (
        <Button asChild variant="ghost" size="sm">
          <Link to={peopleHref}>{t('invitations.openPerson')}</Link>
        </Button>
      ) : null}
      {more.length > 0 ? (
        <RowActionsMenu
          label={t('invitations.rowActions', { email: invitation.email })}
          actions={more}
        />
      ) : null}
    </span>
  );
}

export function InvitationItem({
  invitation,
  actions,
  onEditRoles,
  peopleHref,
  cardHref,
}: {
  readonly invitation: Invitation;
  readonly actions: Actions;
  readonly onEditRoles: (invitation: Invitation) => void;
  readonly peopleHref: string;
  readonly cardHref: string;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const { status } = invitation;
  const selected = useMatch(cardHref.split('?')[0] ?? cardHref) !== null;
  const when = useWhenText(invitation);

  return (
    <div role="listitem" className={cn('people-row', selected && 'people-row-selected')}>
      <Link
        to={cardHref}
        className="people-row-main"
        aria-current={selected ? 'page' : undefined}
        aria-label={t('invitations.openCard', { email: invitation.email })}
        data-invitation-link={invitation.id}
      >
        <Avatar name={invitation.email} invitation />
        <span className="flex min-w-0 flex-col">
          <span className="people-row-name">{invitation.email}</span>
          <span className="people-row-sub">{invitation.displayName ?? t('people.noName')}</span>
        </span>
      </Link>
      <span className="people-row-roles">
        {invitation.roles.map((role) => (
          <span key={role.id} className="role-chip">
            {name(role)}
          </span>
        ))}
      </span>
      <span className="people-row-status">
        <span className="flex flex-col items-start gap-1">
          <span className={cn('status-badge', INVITATION_TONE[status])}>
            {t(`invitations.status.${status}`)}
          </span>
          {when !== null ? <span className="text-muted-foreground text-[12px]">{when}</span> : null}
        </span>
      </span>
      <span className="people-row-updated">
        {t('invitations.sentBy', {
          date: formatDate(invitation.createdAt),
          by: invitation.invitedBy,
        })}
      </span>
      <InvitationRowActions
        invitation={invitation}
        actions={actions}
        onEditRoles={onEditRoles}
        peopleHref={peopleHref}
      />
    </div>
  );
}
